<template>
  <Teleport to="body">
    <div
      v-if="visible && src"
      class="image-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label="图片预览"
      @click.self="onBackdropClick"
      @wheel.prevent="onWheel"
    >
      <div class="image-lightbox__toolbar" @click.stop>
        <button
          type="button"
          class="image-lightbox__btn"
          title="逆时针旋转 90°"
          :disabled="busy"
          @click="rotateBy(-90)"
        >
          ↺
        </button>
        <button
          type="button"
          class="image-lightbox__btn"
          title="顺时针旋转 90°"
          :disabled="busy"
          @click="rotateBy(90)"
        >
          ↻
        </button>
        <button
          type="button"
          class="image-lightbox__btn"
          title="缩小"
          :disabled="busy || scale <= minScale"
          @click="zoomBy(1 / ZOOM_STEP)"
        >
          −
        </button>
        <span class="image-lightbox__zoom">{{ zoomPercentLabel }}</span>
        <button
          type="button"
          class="image-lightbox__btn"
          title="放大"
          :disabled="busy || scale >= MAX_SCALE"
          @click="zoomBy(ZOOM_STEP)"
        >
          +
        </button>
        <button
          type="button"
          class="image-lightbox__btn image-lightbox__btn--wide"
          :title="fitMode ? '1:1 还原' : '适应屏幕'"
          :disabled="busy"
          @click="toggleFitMode"
        >
          {{ fitMode ? '1:1' : '适应屏幕' }}
        </button>
        <span v-if="busy" class="image-lightbox__status">保存中…</span>
        <button
          type="button"
          class="image-lightbox__btn image-lightbox__btn--close"
          title="关闭"
          aria-label="关闭"
          @click="requestClose"
        >
          ✕
        </button>
      </div>
      <div
        ref="stageRef"
        class="image-lightbox__stage"
        :class="{ 'is-panning': panning, 'is-grabbable': true }"
        @pointerdown="onPointerDown"
        @pointermove="onPointerMove"
        @pointerup="onPointerUp"
        @pointercancel="onPointerUp"
      >
        <img
          ref="imgRef"
          class="image-lightbox__img"
          :src="src"
          :alt="fileName || '预览'"
          :style="imageStyle"
          draggable="false"
          @load="onImageLoad"
        />
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { ElMessage } from 'element-plus';
import {
  IMAGE_LIGHTBOX_SAVE_DEBOUNCE_MS,
  rotateImageBlob,
  rotateRenderedImage,
} from '@/utils/rotateImageBlob';

const MIN_SCALE = 0.1;
const MAX_SCALE = 8;
const ZOOM_STEP = 1.25;

const props = withDefaults(defineProps<{
  visible: boolean;
  src: string;
  fileName?: string;
  canEdit?: boolean;
  saving?: boolean;
}>(), {
  fileName: '',
  canEdit: false,
  saving: false,
});

const emit = defineEmits<{
  close: [];
  save: [file: File];
}>();

const stageRef = ref<HTMLElement | null>(null);
const imgRef = ref<HTMLImageElement | null>(null);
const rotation = ref(0);
const scale = ref(1);
const translateX = ref(0);
const translateY = ref(0);
const fitMode = ref(true);
const naturalW = ref(0);
const naturalH = ref(0);
const panning = ref(false);
const persisting = ref(false);
const movedDuringPan = ref(false);

let panStartX = 0;
let panStartY = 0;
let panOrigX = 0;
let panOrigY = 0;
let saveTimer: ReturnType<typeof setTimeout> | null = null;
let saveGeneration = 0;
let resizeObserver: ResizeObserver | null = null;

const busy = computed(() => persisting.value || props.saving);

const displaySize = computed(() => {
  const swap = rotation.value === 90 || rotation.value === 270;
  return {
    width: swap ? naturalH.value : naturalW.value,
    height: swap ? naturalW.value : naturalH.value,
  };
});

const fitScale = computed(() => {
  const stage = stageRef.value;
  const { width, height } = displaySize.value;
  if (!stage || width <= 0 || height <= 0) return 1;
  const pad = 24;
  const vw = Math.max(80, stage.clientWidth - pad);
  const vh = Math.max(80, stage.clientHeight - pad);
  return Math.max(MIN_SCALE, Math.min(vw / width, vh / height));
});

const minScale = computed(() => Math.min(MIN_SCALE, fitScale.value * 0.2));

const zoomPercentLabel = computed(() => `${Math.round(scale.value * 100)}%`);

const imageStyle = computed(() => ({
  width: naturalW.value ? `${naturalW.value}px` : undefined,
  height: naturalH.value ? `${naturalH.value}px` : undefined,
  transform: `translate(${translateX.value}px, ${translateY.value}px) rotate(${rotation.value}deg) scale(${scale.value})`,
}));

function clearSaveTimer() {
  if (saveTimer != null) {
    clearTimeout(saveTimer);
    saveTimer = null;
  }
}

function resetView(keepRotation = false) {
  if (!keepRotation) rotation.value = 0;
  translateX.value = 0;
  translateY.value = 0;
  fitMode.value = true;
  scale.value = fitScale.value;
}

function applyFitScale() {
  if (!fitMode.value) return;
  translateX.value = 0;
  translateY.value = 0;
  scale.value = fitScale.value;
}

function clampScale(next: number) {
  return Math.min(MAX_SCALE, Math.max(minScale.value, next));
}

function rotateBy(delta: number) {
  if (busy.value) return;
  rotation.value = ((rotation.value + delta) % 360 + 360) % 360;
  if (fitMode.value) {
    applyFitScale();
  }
  scheduleSave();
}

function zoomBy(factor: number, origin?: { x: number; y: number }) {
  const oldScale = scale.value;
  const next = clampScale(oldScale * factor);
  if (next === oldScale) return;
  if (origin) {
    const k = next / oldScale;
    translateX.value = origin.x - (origin.x - translateX.value) * k;
    translateY.value = origin.y - (origin.y - translateY.value) * k;
  } else {
    translateX.value *= next / oldScale;
    translateY.value *= next / oldScale;
  }
  scale.value = next;
  fitMode.value = Math.abs(next - fitScale.value) < 0.01;
}

function toggleFitMode() {
  if (busy.value) return;
  if (fitMode.value) {
    fitMode.value = false;
    scale.value = 1;
    translateX.value = 0;
    translateY.value = 0;
    return;
  }
  fitMode.value = true;
  applyFitScale();
}

function onWheel(ev: WheelEvent) {
  const factor = ev.deltaY < 0 ? ZOOM_STEP : 1 / ZOOM_STEP;
  const stage = stageRef.value;
  if (!stage) {
    zoomBy(factor);
    return;
  }
  const rect = stage.getBoundingClientRect();
  zoomBy(factor, {
    x: ev.clientX - rect.left - rect.width / 2,
    y: ev.clientY - rect.top - rect.height / 2,
  });
}

function onPointerDown(ev: PointerEvent) {
  if (ev.button !== 0) return;
  panning.value = true;
  movedDuringPan.value = false;
  panStartX = ev.clientX;
  panStartY = ev.clientY;
  panOrigX = translateX.value;
  panOrigY = translateY.value;
  (ev.currentTarget as HTMLElement).setPointerCapture?.(ev.pointerId);
}

function onPointerMove(ev: PointerEvent) {
  if (!panning.value) return;
  const dx = ev.clientX - panStartX;
  const dy = ev.clientY - panStartY;
  if (Math.abs(dx) > 3 || Math.abs(dy) > 3) movedDuringPan.value = true;
  translateX.value = panOrigX + dx;
  translateY.value = panOrigY + dy;
}

function onPointerUp() {
  panning.value = false;
}

function onBackdropClick() {
  if (movedDuringPan.value) {
    movedDuringPan.value = false;
    return;
  }
  void requestClose();
}

function onImageLoad() {
  const img = imgRef.value;
  naturalW.value = img?.naturalWidth || 0;
  naturalH.value = img?.naturalHeight || 0;
  void nextTick(() => {
    applyFitScale();
    observeStage();
  });
}

function observeStage() {
  resizeObserver?.disconnect();
  const stage = stageRef.value;
  if (!stage || typeof ResizeObserver === 'undefined') return;
  resizeObserver = new ResizeObserver(() => {
    if (fitMode.value) applyFitScale();
  });
  resizeObserver.observe(stage);
}

function scheduleSave() {
  if (!props.canEdit) return;
  clearSaveTimer();
  if (rotation.value === 0) return;
  const gen = ++saveGeneration;
  saveTimer = setTimeout(() => {
    saveTimer = null;
    void persistRotation(gen);
  }, IMAGE_LIGHTBOX_SAVE_DEBOUNCE_MS);
}

async function persistRotation(gen = saveGeneration) {
  if (!props.canEdit || rotation.value === 0 || busy.value) return;
  if (gen !== saveGeneration) return;
  persisting.value = true;
  try {
    const img = imgRef.value;
    let file: File;
    try {
      file = img && img.naturalWidth > 0
        ? await rotateRenderedImage(img, rotation.value, props.fileName)
        : await rotateImageBlob(props.src, rotation.value, props.fileName);
    } catch {
      file = await rotateImageBlob(props.src, rotation.value, props.fileName);
    }
    if (gen !== saveGeneration) return;
    emit('save', file);
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : String(e || '');
    ElMessage.error(/source image could not be decoded/i.test(msg)
      ? '图片解码失败，请重新打开后再旋转'
      : (msg || '旋转保存失败'));
  } finally {
    persisting.value = false;
  }
}

async function requestClose() {
  clearSaveTimer();
  if (props.canEdit && rotation.value !== 0 && !busy.value) {
    await persistRotation();
  }
  emit('close');
}

function onKeydown(ev: KeyboardEvent) {
  if (ev.key === 'Escape') {
    ev.preventDefault();
    void requestClose();
  }
}

watch(
  () => [props.visible, props.src] as const,
  ([visible]) => {
    clearSaveTimer();
    saveGeneration += 1;
    if (!visible) {
      resizeObserver?.disconnect();
      resizeObserver = null;
      panning.value = false;
      persisting.value = false;
      return;
    }
    resetView();
    void nextTick(() => observeStage());
  },
  { flush: 'post' },
);

watch(
  () => props.visible,
  (visible) => {
    if (visible) {
      window.addEventListener('keydown', onKeydown);
      document.body.style.overflow = 'hidden';
    } else {
      window.removeEventListener('keydown', onKeydown);
      document.body.style.overflow = '';
    }
  },
);

onBeforeUnmount(() => {
  clearSaveTimer();
  resizeObserver?.disconnect();
  window.removeEventListener('keydown', onKeydown);
  document.body.style.overflow = '';
});

defineExpose({
  rotateBy,
  zoomBy,
  toggleFitMode,
  persistRotation,
  requestClose,
});
</script>

<style scoped>
.image-lightbox {
  position: fixed;
  inset: 0;
  z-index: 10050;
  display: flex;
  flex-direction: column;
  background: rgba(0, 0, 0, 0.78);
  box-sizing: border-box;
  user-select: none;
}

.image-lightbox__toolbar {
  position: relative;
  z-index: 2;
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px 56px 10px;
}

.image-lightbox__btn {
  min-width: 36px;
  height: 36px;
  padding: 0 10px;
  border: none;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.92);
  color: #1f2329;
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
}

.image-lightbox__btn:hover:not(:disabled) {
  background: #fff;
}

.image-lightbox__btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.image-lightbox__btn--wide {
  min-width: 76px;
  font-size: 13px;
}

.image-lightbox__btn--close {
  position: absolute;
  right: 16px;
  top: 12px;
  width: 36px;
  border-radius: 50%;
  padding: 0;
}

.image-lightbox__zoom,
.image-lightbox__status {
  min-width: 48px;
  color: #fff;
  font-size: 13px;
  text-align: center;
}

.image-lightbox__stage {
  flex: 1 1 auto;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: grab;
}

.image-lightbox__stage.is-panning {
  cursor: grabbing;
}

.image-lightbox__img {
  max-width: none;
  max-height: none;
  transform-origin: center center;
  will-change: transform;
  pointer-events: none;
}
</style>
